import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"

import { Modal, Spinner } from "react-bootstrap"
import { multipleUploadFile } from "requesters/jobServiceRequester"
import Select from "react-select"
import SweetAlert from "react-bootstrap-sweetalert"
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../../reducers"

import {
  MAX_SIZE_UPLOADED_FILE,
  MAX_SIZE_UPLOADED_FILE_TEXT,
  ADMIN
} from "../../../../../utils/constants"
import Dropzone from "../../../../../components/utils/Dropzone"

const AddFilesModal = ({
  show,
  onHide,
  jobService,
  reloadData,
  fileCategories,
  defaultCategory,
  showUploadedFilesSection,
  uploadedFilesSectionSize,
  uploadedFilesSource,
  assignment,
  preloadedFile = null
}) => {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const isAdmin = currentRole.includes(ADMIN)
  const { id, attributes: { surveyorFiles } } = jobService

  const [fileCategory, setFileCategory] = useState("")
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [customFilename, setCustomFilename] = useState("")
  const [largeUploads, setLargeUploads] = useState([])
  const [uploading, setUploading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [filesCollectionError, setFilesCollectionError] = useState(false)
  const [uploadedPhoto, setUploadedPhoto] = useState([])

  const handlePreview = e => {
    setUploadedPhoto([])
    const files = e.value
    const largeFiles = files?.filter(file => file.size > MAX_SIZE_UPLOADED_FILE)
    const filesCollectionSize = files?.map(file => file.size).reduce((result, size) => result + size)

    if (largeFiles?.length) {
      setLargeUploads(largeFiles)
      setUploadedPhoto([])
      setShowError(true)
    } else if (filesCollectionSize > MAX_SIZE_UPLOADED_FILE) {
      setUploadedPhoto([])
      setFilesCollectionError(true)
    } else {
      setUploadedFiles(files)
    }
  }

  const multiplePhotsUpload = () => {
    const formData = new FormData();
    
    // Handle files from dropzone (uploadedPhoto)
    uploadedPhoto.forEach((file, index) => {
        formData.append(`file-${index}`, file);
        formData.append("document_category", fileCategory)
        formData.append("created_by_user_id", currentUser.id)
        if (assignment) {
          formData.append("surveyor_assignment_id", assignment.surveyorAssignmentId)
        }
      });
    
    // Handle preloaded files (uploadedFiles) - for exported PDFs
    if (uploadedFiles.length > 0 && uploadedPhoto.length === 0) {
      uploadedFiles.forEach((file, index) => {
        // Create a new File with the custom filename if it differs
        const finalFile = customFilename && customFilename !== file.name
          ? new File([file], customFilename, { type: file.type })
          : file
        formData.append(`file-${index}`, finalFile);
        formData.append("document_category", fileCategory)
        formData.append("created_by_user_id", currentUser.id)
        if (assignment) {
          formData.append("surveyor_assignment_id", assignment.surveyorAssignmentId)
        }
      });
    }
    
      setUploading(true)
      multipleUploadFile(baseUrl, id, formData)
      .then(res => {
        if (res.error) return
        setUploading(false)
        setUploadedPhoto([])
        setUploadedFiles([])
        reloadData()
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => {
        setUploading(false)
      });
  }

  useEffect(() => {
    if (show) {
      setFileCategory(defaultCategory)
      // If a preloaded file is provided, set it as the uploaded file
      if (preloadedFile) {
        setUploadedFiles([preloadedFile])
        setCustomFilename(preloadedFile.name || "exported_document.pdf")
        // Use defaultCategory if provided, otherwise fall back to "Other" for exported PDFs
        if (!defaultCategory) {
          setFileCategory("Other")
        }
      }
    } else {
      setUploadedFiles([])
      setUploadedPhoto([])
      setFileCategory("")
      setCustomFilename("")
      setShowError(false)
      setLargeUploads([])
      setFilesCollectionError(false)
    }
  }, [show, defaultCategory, preloadedFile])

  const onSubmit = () => {
    multiplePhotsUpload()
  }

  const defaultValue = fileCategories.find(x => x.value === fileCategory)
  const filteredUploadedFilesSource = assignment && !isAdmin ? uploadedFilesSource.filter(file => (file.data?.attributes?.createdByUserId === assignment?.surveyorId)) : uploadedFilesSource

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <h2 className="modal-title">ADD FILES</h2>
      </Modal.Header>
      <div className="modal-body px-md-5">
        <form>
          <div className="form-group row mb-3">
            <label className="col-3 pt-2">File Category*</label>
            <div className="col-8">
              <div className="custom-file">
                <Select
                  name="file_category"
                  isSearchable
                  value={defaultValue}
                  onChange={e => setFileCategory(e.value)}
                  options={fileCategories}
                  placeholder="Select a file category" />
              </div>
            </div>
          </div>
          {preloadedFile && (
            <div className="form-group row mb-3">
              <label className="col-3 pt-2">Filename*</label>
              <div className="col-8">
                <input
                  type="text"
                  className="form-control"
                  value={customFilename}
                  onChange={e => setCustomFilename(e.target.value)}
                  placeholder="Enter filename" />
                <small className="text-muted">
                  The filename for the exported PDF
                </small>
              </div>
            </div>
          )}
        </form>

        <div className="mt-2">
          {!preloadedFile && (
            <div className="form-group mt-4">
              <Dropzone
                wrapperClass="drag-drop"
                multiple
                name="photo"
                fileTypes={fileCategories[0]?.value === "Photo" && {
                  "image/jpeg": [".jpeg"], "image/jpg": [".jpg"], "image/png": [".png"], "image/gif": [".gif"]
                }}
                fileTypeNames=".jpeg .png .jpg .gif"
                setUploadedFiles={setUploadedPhoto}
                handleFileUpload={handlePreview} />
            </div>
          )}
          <div className="mt-4">
            {!!uploadedPhoto?.length && uploadedFiles.map(file => (
              <p key={file.path} className="file-list-item mb-0">
                <i className="bi bi-file-earmark mr-2"></i>
                {file.path}
              </p>
            ))}
            {!!uploadedFiles?.length && !uploadedPhoto?.length && uploadedFiles.map(file => (
              <p key={file.name} className="file-list-item mb-0">
                <i className="bi bi-file-earmark mr-2"></i>
                {file.name}
              </p>
            ))}
          </div>
        </div>
        <hr />

        {(showUploadedFilesSection && filteredUploadedFilesSource?.length > 0) && (
          <>
            <p>Uploaded Files</p>
            <div className="ml-0 mt-1">
              {!!filteredUploadedFilesSource?.length && filteredUploadedFilesSource.map(
                file => (
                  <div key={file?.data?.id} className="row mb-0">
                    <div className={`${uploadedFilesSectionSize === "full" ? "col-7" : "col-5"}`}>
                      <i className="bi bi-file-earmark mr-2"></i>
                      {file?.data?.attributes?.fileName}
                    </div>
                    <div className={`${uploadedFilesSectionSize === "full" ? "col-5" : "col-3"}`}>
                      {file?.data?.attributes?.documentCategory}
                    </div>
                  </div>
                )
              )}
            </div>
          </>
        )}


        <div className="modal-footer d-flex justify-content-end px-0 py-4">
          <button
            className="btn btn-link btn-lg"
            onClick={onHide}
            type="submit">
            Close
          </button>
          <button
            className="btn btn-red btn-lg"
            disabled={
              !fileCategory
              || (!uploadedPhoto?.length && !uploadedFiles?.length)
              || largeUploads?.length
              || (preloadedFile && !customFilename?.trim())
            }
            onClick={onSubmit}
            type="submit">
            {uploading ? (
              <Spinner
                animation="border" />
            )
            : (
              `Save`
            )}
          </button>
        </div>
      </div>

      <SweetAlert
        show={showError}
        type="error"
        title="File size exceeded!"
        closeOnClickOutside
        allowEscape={false}
        onConfirm={() => {
          setShowError(false)
          setLargeUploads([])
          }}>
        Max size is {MAX_SIZE_UPLOADED_FILE_TEXT}
      </SweetAlert>

      <SweetAlert
        show={filesCollectionError}
        type="error"
        title="File sizes exceeded!"
        closeOnClickOutside
        allowEscape={false}
        onConfirm={() => setFilesCollectionError(false)}>
        Max size for a collection of files is {MAX_SIZE_UPLOADED_FILE_TEXT}
      </SweetAlert>
    </Modal>
  )
  }
export default AddFilesModal
